import { useState, type RefObject } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FolderPlus, FolderSync } from "lucide-react";
import {
  Button,
  DataTable,
  DialogClose,
  Field,
  FormDrawer,
  Input,
  TableIdentity,
  localTablePage,
  type DataTableColumn,
  type DataTableState,
} from "@h2j-data-team/ui";
import type { GroupWithCounts, QlikApp } from "@repo/shared";

import { toast } from "@/components/shared";
import { getErrorMessage } from "@/lib/api-client";
import { tableMessages, tableSearch } from "@/lib/ui-labels";
import { useTableDensity } from "@/lib/use-table-density";
import { groupsQueryOptions, useBulkAddGroupApps, useCreateGroup } from "@/features/groups/api";

interface AddAppsToGroupDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionId: string;
  /** The currently-selected apps to add. */
  apps: QlikApp[];
  /** Called after a successful add so the caller can clear its selection. */
  onAdded: () => void;
  /** The control that opened the drawer: focus goes back to it on close. */
  restoreFocusRef?: RefObject<HTMLElement | null>;
}

/** Picks an existing sync group (or creates one) and bulk-adds the selected apps to it. */
export function AddAppsToGroupDrawer({
  open,
  onOpenChange,
  connectionId,
  apps,
  onAdded,
  restoreFocusRef,
}: AddAppsToGroupDrawerProps) {
  const { t } = useTranslation("single-app");
  const { t: tg } = useTranslation("sync-groups");
  const { t: tc } = useTranslation("common");
  const { density } = useTableDensity();
  const { data: groupsResponse, isLoading } = useQuery(groupsQueryOptions());
  const groups = groupsResponse?.data ?? [];
  const [search, setSearch] = useState("");
  const [newName, setNewName] = useState("");
  const bulkAdd = useBulkAddGroupApps();
  const createGroup = useCreateGroup();
  const pending = bulkAdd.isPending || createGroup.isPending;

  const payload = {
    apps: apps.map((a) => ({
      connectionId,
      appId: a.id,
      appName: a.name,
      spaceName: a.spaceName ?? null,
      ownerName: a.ownerName ?? null,
    })),
  };

  const addToGroup = async (groupId: string) => {
    try {
      const res = await bulkAdd.mutateAsync({ groupId, data: payload });
      toast.success(t("apps.bulk.success", { added: res.added, skipped: res.skipped }));
      onOpenChange(false);
      onAdded();
    } catch (err) {
      toast.error(getErrorMessage(err, t("apps.bulk.failed")));
    }
  };

  const createAndAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const group = await createGroup.mutateAsync({ name });
      setNewName("");
      await addToGroup(group.id);
    } catch (err) {
      toast.error(getErrorMessage(err, t("apps.bulk.failed")));
    }
  };

  const columns: DataTableColumn<GroupWithCounts>[] = [
    {
      id: "name",
      header: tg("table.name"),
      rowHeader: true,
      rowLink: true,
      variant: "identity",
      value: (group) => group.name,
      cell: (group) => (
        <TableIdentity
          leading={<FolderSync />}
          name={
            <button
              type="button"
              disabled={pending}
              aria-label={tg("apps.addSelectedTo", { name: group.name })}
              onClick={() => void addToGroup(group.id)}
              className="max-w-full truncate hover:underline disabled:cursor-not-allowed disabled:no-underline"
            >
              {group.name}
            </button>
          }
        />
      ),
    },
    {
      id: "apps",
      header: tg("table.apps"),
      align: "end",
      width: 110,
      cell: (group) => t("apps.bulk.groupApps", { count: group.appCount }),
    },
  ];

  // The picker shows every group at once; only the search narrows it.
  const state: DataTableState = {
    page: 1,
    pageSize: Math.max(groups.length, 1),
    query: search,
    filters: {},
    selection: [],
  };
  const page = localTablePage(groups, state, { columns, search: (group) => group.name });

  return (
    <FormDrawer
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      closeLabel={tc("close")}
      title={t("apps.bulk.pickTitle")}
      description={t("apps.bulk.pickDescription", { count: apps.length })}
      restoreFocusRef={restoreFocusRef}
      busy={pending}
      footer={
        <DialogClose asChild>
          <Button type="button" variant="outline">
            {t("apps.bulk.cancel")}
          </Button>
        </DialogClose>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <Field label={t("apps.bulk.newGroup")}>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={t("apps.bulk.newGroupPlaceholder")}
              />
            </Field>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={createAndAdd}
            loading={createGroup.isPending}
            disabled={!newName.trim() || pending}
          >
            <FolderPlus />
            {t("apps.bulk.createAdd")}
          </Button>
        </div>

        <DataTable
          label={t("apps.bulk.pickTitle")}
          density={density}
          columns={columns}
          rows={page.rows}
          getRowId={(group) => group.id}
          state={state}
          onStateChange={(next) => setSearch(next.query)}
          loading={isLoading}
          search={tableSearch(t("apps.bulk.searchGroups"))}
          messages={tableMessages({
            empty: { icon: <FolderSync />, title: t("apps.bulk.noGroups") },
          })}
        />
      </div>
    </FormDrawer>
  );
}
